import axios from "axios";

const EPSON_VENDOR_ID = 0x04b8;

let device = null;
let outEndpoint = null;
let inEndpoint = null;
let interfaceNumber = null;
let targetSerialNumber = null; // set by pair() or silentConnect(); handlers are no-ops until set

function resetState() {
	device = null;
	outEndpoint = null;
	inEndpoint = null;
	interfaceNumber = null;
}

async function connectDevice(usbDevice) {
	try {
		await usbDevice.open();

		if (usbDevice.configuration === null) {
			await usbDevice.selectConfiguration(1);
		}

		let outEndpointNumber = null;
		let inEndpointNumber = null;
		let claimedInterfaceNumber = null;

		for (const usbInterface of usbDevice.configuration.interfaces) {
			for (const alternate of usbInterface.alternates) {
				for (const endpoint of alternate.endpoints) {
					if (endpoint.type === 'bulk') {
						if (endpoint.direction === 'out') {
							outEndpointNumber = endpoint.endpointNumber;
							claimedInterfaceNumber = usbInterface.interfaceNumber;
						}
						if (endpoint.direction === 'in') {
							inEndpointNumber = endpoint.endpointNumber;
						}
					}
				}
			}
		}

		if (outEndpointNumber === null) {
			await usbDevice.close();
			throw new Error('WebUSB: no bulk-OUT endpoint found on Epson printer');
		}

		await usbDevice.claimInterface(claimedInterfaceNumber);

		device = usbDevice;
		outEndpoint = outEndpointNumber;
		inEndpoint = inEndpointNumber;
		interfaceNumber = claimedInterfaceNumber;
	} catch (e) {
		try {
			await usbDevice.close();
		} catch (_) {}

		throw e;
	}
}

async function closeDevice() {
	if (!device) {
		return;
	}

	const usbDevice = device;
	const claimedInterfaceNumber = interfaceNumber;
	resetState();

	try {
		await usbDevice.releaseInterface(claimedInterfaceNumber);
	} catch (_) {}

	try {
		await usbDevice.close();
	} catch (_) {}
}

async function fetchBytes(url, config) {
	const res = await axios.get(url, Object.assign({}, config, { responseType: 'arraybuffer' }));

	return {
		status: res.status,
		data: new Uint8Array(res.data),
	};
}

async function send(bytes) {
	if (!device) {
		throw new Error('Printer not connected');
	}

	const result = await device.transferOut(outEndpoint, bytes);

	if (result.status !== 'ok') {
		throw new Error('transferOut status: ' + result.status);
	}
}

if ('usb' in navigator) {
	navigator.usb.addEventListener('connect', async (e) => {
		if (
			e.device.vendorId === EPSON_VENDOR_ID &&
			targetSerialNumber &&
			e.device.serialNumber === targetSerialNumber &&
			!device
		) {
			try {
				await connectDevice(e.device);
			} catch (_) {}
		}
	});

	navigator.usb.addEventListener('disconnect', (e) => {
		if (device && e.device === device) {
			resetState();
		}
	});
}

export const webusbPrinter = {
    connectedSerial() { return device?.serialNumber || null; },
    async printKitchenBytes(bytes) { await send(bytes); },
	async pair() {
		if (!('usb' in navigator)) {
			throw new Error('WebUSB not supported in this browser');
		}

		const usbDevice = await navigator.usb.requestDevice({ filters: [{ vendorId: EPSON_VENDOR_ID }] });
		await connectDevice(usbDevice);
		targetSerialNumber = usbDevice.serialNumber;

		return {
			serial: usbDevice.serialNumber,
			name: usbDevice.productName || ''
		};
	},

	async silentConnect(serialNumber) {
		if (!('usb' in navigator)) {
			return;
		}

		targetSerialNumber = serialNumber;

		if (device) {
			return;
		}

		const devices = await navigator.usb.getDevices();
		const usbDevice = devices.find(d => d.vendorId === EPSON_VENDOR_ID && d.serialNumber === serialNumber);

		if (!usbDevice) {
			return;
		}

		try {
			await connectDevice(usbDevice);
		} catch (_) {}
	},

	isConnected() {
		return device !== null;
	},

	async disconnect() {
		await closeDevice();
	},

	async printInvoice(orderId) {
		const { data } = await fetchBytes(`/web-print/orders/${orderId}/invoice`);
		await send(data);
	},

	async printRunnerChit(orderId) {
		const { data, status } = await fetchBytes(`/web-print/orders/${orderId}/runner-chit`);
		if (status !== 204 && data.byteLength > 0) {
			await send(data);
		}
	},

	async printTest(printerId) {
		const { data } = await fetchBytes('/web-print/test-page', { params: { printer_id: printerId } });
		await send(data);
	},

	async printFinancialReport(params) {
		const { data } = await fetchBytes('/web-print/reports/financial', { params });
		await send(data);
	},

	async printOpenChecksReport(params) {
		const { data } = await fetchBytes('/web-print/reports/open-checks', { params });
		await send(data);
	},

	async printClosedChecksReport(params) {
		const { data } = await fetchBytes('/web-print/reports/closed-checks', { params });
		await send(data);
	},
};
